import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useShop } from "@/contexts/ShopContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, TrendingDown, X, BellOff } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import RetestAllButton from "@/components/RetestAllButton";

export default function AlertsPanel() {
  const { t } = useLang();
  const { selectedShopId } = useShop();
  const qc = useQueryClient();

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ["alerts", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/alerts`)).data,
    enabled: !!selectedShopId,
  });

  const dismiss = useMutation({
    mutationFn: async (alertId) =>
      (await api.post(`/shops/${selectedShopId}/alerts/${alertId}/dismiss`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts", selectedShopId] });
      qc.invalidateQueries({ queryKey: ["alerts-summary"] });
    },
  });

  const dismissAll = useMutation({
    mutationFn: async () =>
      (await api.post(`/shops/${selectedShopId}/alerts/dismiss-all`)).data,
    onSuccess: (data) => {
      toast.success(t("alerts_dismissed_all").replace("{n}", data.dismissed));
      qc.invalidateQueries({ queryKey: ["alerts", selectedShopId] });
      qc.invalidateQueries({ queryKey: ["alerts-summary"] });
    },
  });

  if (isLoading || alerts.length === 0) return null;

  const critical = alerts.filter((a) => a.severity === "critical");
  const warning = alerts.filter((a) => a.severity === "warning");

  return (
    <section
      data-testid="alerts-panel"
      className="border border-destructive/30 bg-destructive/[0.03] rounded-lg overflow-hidden"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-destructive/20 bg-destructive/[0.05]">
        <div className="flex items-center gap-2.5 flex-wrap">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <h3 className="font-display text-base font-medium">
            {t("alerts_title")}
          </h3>
          <Badge variant="destructive" className="font-mono text-[10px] tracking-widest">
            {alerts.length}
          </Badge>
          {critical.length > 0 && (
            <span className="text-xs font-mono text-muted-foreground">
              {critical.length} {t("alerts_critical")} · {warning.length} {t("alerts_warning")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RetestAllButton shopId={selectedShopId} />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => dismissAll.mutate()}
            disabled={dismissAll.isPending}
            data-testid="alerts-dismiss-all"
            className="gap-1.5 text-xs"
          >
            <BellOff className="h-3.5 w-3.5" />
            {t("alerts_dismiss_all")}
          </Button>
        </div>
      </header>

      <ul className="divide-y divide-border">
        {alerts.map((a) => (
          <li
            key={a.alert_id}
            data-testid={`alert-row-${a.alert_id}`}
            className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-muted/40 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div
                className={cn(
                  "h-2 w-2 rounded-full shrink-0",
                  a.severity === "critical" ? "bg-destructive" : "bg-[hsl(var(--chart-4))]",
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{a.keyword}</div>
                <div className="text-xs text-muted-foreground font-mono mt-0.5 flex items-center gap-1.5">
                  <TrendingDown className="h-3 w-3 text-destructive" />
                  #{a.from_position} → #{a.to_position}
                  <span className="text-destructive">(+{a.drop_delta})</span>
                  <span className="ml-2 text-muted-foreground/70">
                    {new Date(a.detected_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => dismiss.mutate(a.alert_id)}
              disabled={dismiss.isPending}
              data-testid={`alert-dismiss-${a.alert_id}`}
              className="h-7 w-7 p-0"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
